import React, { useState } from "react";
import {
  createStyles,
  Grid,
  makeStyles,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Button,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import ArrowDropDown from "@material-ui/icons/ArrowDropDown";
import ArrowDropUpIcon from '@material-ui/icons/ArrowDropUp';

import WithLoading from "../../../../utils/WithLoading";
import CardCommon from "../../../../components/card/CardCommon";
import { getIsAuthorized } from "../../../../utils/authorities";
import Authorities from "../../../../auth/authorities";

export interface VoidSummeryInfoNodeProps {
  isLoading: any;
  logsNode: any;
}

const useStyles = makeStyles(() =>
  createStyles({
    bulletItem: { display: "block" },
    centeredItem: { display: "flex", alignItems: "center", height: "100%" },
    summaryWrapper: {
      display: "flex",
      alignItems: "center",
      justifyContent: "flex-end",
      height: "100%",
      paddingRight: 12,
    },
    viewButton: {
      fontSize: "0.85rem",
      textTransform: "none",
    },
  }),
);

const LogsDetailsInfoNode: React.FC<VoidSummeryInfoNodeProps> = ({
  isLoading,
  logsNode,
}) => {
  const classes = useStyles();
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogContent, setDialogContent] = useState<any>(null);
  const [logDetails, setLogDetails] = useState<any>(null);
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");

  const safeJsonParse = (input: any) => {
    if (typeof input === "string") {
      try {
        return JSON.parse(input);
      } catch {
        return input;
      }
    }
    return input;
  };

  const formatSummaryString = (text: string) => {
    const parts = text?.split(/(`[^`]+`)/g);
    return parts?.map((part, i) => {
      if (part.startsWith("`") && part.endsWith("`")) {
        const value = part.slice(1, -1);
        try {
          const parsed = JSON.parse(value);
          return (
            <pre
              key={i}
              style={{
                margin: 0,
                fontWeight: "bold",
                fontFamily: "monospace",
                whiteSpace: "pre-wrap",
                textAlign: "right",
              }}
            >
              {JSON.stringify(parsed, null, 2)}
            </pre>
          );
        } catch {
          return <strong key={i}>{value}</strong>;
        }
      }
      return <span key={i}>{part}</span>;
    });
  };

  const handleOpenDialog = (summary: any, log: any) => {
    setDialogContent(summary);
    setOpenDialog(true);
    setLogDetails(log);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setDialogContent(null);
  };

  const tryParseJSON = (value: string) => {
    try {
      const parsed = JSON.parse(value);
      const style = {
        margin: 0,
        paddingLeft: "20px",
        textAlign: "left" as const,
        wordBreak: "break-word" as const,
        whiteSpace: "pre-wrap" as const,
        fontFamily: "monospace",
      };

      if (Array.isArray(parsed)) {
        return (
          <ul style={style}>
            {parsed.map((item, index) => (
              <li key={index}>
                {typeof item === "object" ? (
                  <pre style={{ margin: 0 }}>
                    {JSON.stringify(item, null, 2)}
                  </pre>
                ) : (
                  String(item)
                )}
              </li>
            ))}
          </ul>
        );
      }

      if (typeof parsed === "object" && parsed !== null) {
        return (
          <ul style={style}>
            {Object.entries(parsed).map(([key, val]) => (
              <li key={key}>
                <strong>{key}:</strong>{" "}
                {typeof val === "object"
                  ? JSON.stringify(val, null, 2)
                  : String(val)}
              </li>
            ))}
          </ul>
        );
      }

      return String(parsed);
    } catch {
      return value;
    }
  };

  const formatFieldName = (text: string) => {
    if (!text) return "";
    const spaced = text.replace(/([A-Z])/g, " $1").trim();
    return spaced
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const formatValue = (val: any) => {
    if (val === null || val === undefined) return "";
    if (typeof val === "string") return val;
    return JSON.stringify(val);
  };

  // Toggle sort order between asc and desc
  const toggleSortOrder = () => {
    setSortOrder((prev) => (prev === "desc" ? "asc" : "desc"));
  };

  // Sort logs by date according to sortOrder
  const sortedLogs = React.useMemo(() => {
    if (!logsNode) return [];
    return [...logsNode].sort((a, b) => {
      const dateA = new Date(a.date).getTime();
      const dateB = new Date(b.date).getTime();
      return sortOrder === "asc" ? dateA - dateB : dateB - dateA;
    });
  }, [logsNode, sortOrder]);

  return (
    <>
      {/* Table Header */}
      <CardCommon
        backgroundColor={"table_header_background"}
        style={{ marginTop: "40px", height: "40px" }}
      >
        <Grid container style={{ alignItems: "center", height: "100%" }}>
          <Grid item xs={2} className={classes.centeredItem}>
            <Typography
              align="left"
              style={{ paddingLeft: "12px", display: "flex", alignItems: "center", cursor: "pointer" }}
              onClick={toggleSortOrder}
            >
              Date
              {sortOrder === "desc" ? (
                <ArrowDropDown style={{ marginLeft: "5px" }} />
              ) : (
                <ArrowDropUpIcon style={{ marginLeft: "5px" }} />
              )}
            </Typography>
          </Grid>
          <Grid
            item
            xs={2}
            className={classes.centeredItem}
            style={{ justifyContent: "center" }}
          >
            <Typography align="center" style={{ justifyContent: "center" }}>
              Changed by
            </Typography>
          </Grid>
          <Grid
            item
            xs={1}
            className={classes.centeredItem}
            style={{ justifyContent: "center" }}
          >
            <Typography align="center" style={{ justifyContent: "center" }}>
              Status
            </Typography>
          </Grid>
          <Grid
            item
            xs={3}
            className={classes.centeredItem}
            style={{ justifyContent: "center" }}
          >
            <Typography align="center" style={{ justifyContent: "center" }}>
              Message
            </Typography>
          </Grid>
          <Grid
            item
            xs={4}
            className={classes.centeredItem}
            style={{ justifyContent: "end" }}
          >
            <Typography align="right" style={{ paddingRight: "12px" }}>
              Changed
            </Typography>
          </Grid>
        </Grid>
      </CardCommon>

      {/* Table Body */}
      <Grid container>
        <Grid item xs={12}>
          {sortedLogs?.map((log: any, index: number) => {
            const parsedSummary = safeJsonParse(log?.summary);
            const summaryArray = Array.isArray(parsedSummary)
              ? parsedSummary
              : [parsedSummary];

            const totalLength = summaryArray.join("").length;

            const formatted = summaryArray.map((s: string, i: number) => (
              <Typography key={i} align="right" className={classes.bulletItem}>
                {formatSummaryString(s)}
              </Typography>
            ));

            return (
              ((getIsAuthorized(Authorities.STOCK_CONFIGURATION_READ) &&
                log.access_type) ||
                !log.access_type) && (
                <CardCommon
                  key={index}
                  backgroundColor={"entity_background"}
                  style={{ padding: "8px 0", marginTop: 4 }}
                >
                  <Grid container alignItems="center">
                    <Grid item xs={2}>
                      <Typography align="left" style={{ paddingLeft: 12 }}>
                        {log.date}
                      </Typography>
                    </Grid>
                    <Grid item xs={2}>
                      <Typography align="center">
                        {log?.metadata?.changedBy}
                      </Typography>
                    </Grid>
                    <Grid item xs={1}>
                      <Typography align="center">{log?.code}</Typography>
                    </Grid>
                    <Grid item xs={3}>
                      <Typography align="center">{log.message}</Typography>
                    </Grid>
                    <Grid item xs={4} className={classes.summaryWrapper}>
                      {totalLength > 60 ? (
                        <Button
                          className={classes.viewButton}
                          onClick={() => handleOpenDialog(summaryArray, log)}
                        >
                          View Changes
                        </Button>
                      ) : (
                        <Grid
                          container
                          direction="column"
                          alignItems="flex-end"
                        >
                          {formatted}
                        </Grid>
                      )}
                    </Grid>
                  </Grid>
                </CardCommon>
              )
            );
          })}
        </Grid>
      </Grid>

      {/* Dialog */}
      {openDialog && (
        <Dialog
          open={openDialog}
          onClose={handleCloseDialog}
          fullWidth
          maxWidth="lg"
        >
          <DialogTitle>
            Full Change Summary
            <IconButton
              onClick={handleCloseDialog}
              style={{ position: "absolute", right: 8, top: 8 }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            <Grid container>
              <Grid item xs={12}>
                <Typography
                  align="left"
                  style={{ paddingBottom: "12px", paddingLeft: "4px" }}
                >
                  {logDetails.code.toUpperCase()}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <CardCommon
                  backgroundColor={"table_header_background"}
                  style={{ padding: "8px 0" }}
                >
                  <Grid container>
                    <Grid item xs={4}>
                      <Typography
                        variant="subtitle2"
                        align="left"
                        style={{ paddingLeft: "12px" }}
                      >
                        Field
                      </Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="subtitle2" align="center">
                        From
                      </Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography
                        variant="subtitle2"
                        align="right"
                        style={{ paddingRight: "12px" }}
                      >
                        To
                      </Typography>
                    </Grid>
                  </Grid>
                </CardCommon>

                {(Array.isArray(dialogContent)
                  ? dialogContent
                  : [dialogContent]
                ).flatMap((item: string, i: number) => {
                  const field = item.split("changed")[0]?.trim();
                  const fromMatch = item.match(/from\s+`?([\s\S]+?)`?\s+to/);
                  const toMatch = item.match(/to\s+`?([\s\S]+?)`?$/);
                  const fromValue = fromMatch
                    ? safeJsonParse(fromMatch[1])
                    : null;
                  const toValue = toMatch ? safeJsonParse(toMatch[1]) : null;

                  const results: any[] = [];

                  const flattenFields = (
                    keyPrefix: string,
                    fromObj: any,
                    toObj: any,
                    depth: number,
                  ) => {
                    const allKeys = new Set([
                      ...Object.keys(fromObj || {}),
                      ...Object.keys(toObj || {}),
                    ]);
                    allKeys.forEach((key) => {
                      if (
                        ["created", "updated", "version"].includes(
                          key.toLowerCase(),
                        )
                      )
                        return; // Skip unwanted keys

                      const nestedFrom = fromObj?.[key];
                      const nestedTo = toObj?.[key];
                      const isObject = (val: any) =>
                        typeof val === "object" &&
                        val !== null &&
                        !Array.isArray(val);
                      if (isObject(nestedFrom) || isObject(nestedTo)) {
                        flattenFields(
                          `${keyPrefix}.${key}`,
                          nestedFrom || {},
                          nestedTo || {},
                          depth + 1,
                        );
                      } else if (nestedFrom !== nestedTo) {
                        results.push({
                          field: `${"-".repeat(depth)} ${key}`,
                          from: nestedFrom,
                          to: nestedTo,
                        });
                      }
                    });
                  };

                  if (
                    typeof fromValue === "object" &&
                    typeof toValue === "object"
                  ) {
                    flattenFields(field, fromValue, toValue, 1);
                  } else {
                    results.push({
                      field: formatFieldName(field),
                      from: fromValue,
                      to: toValue,
                    });
                  }

                  return results.map((row, j) => (
                    <CardCommon
                      key={`${i}-${j}`}
                      backgroundColor="entity_background"
                      style={{ padding: "6px 0", marginTop: 6 }}
                    >
                      <Grid container alignItems="center">
                        <Grid item xs={4}>
                          <Typography align="left" style={{ paddingLeft: 12 }}>
                            {formatFieldName(row.field)}
                          </Typography>
                        </Grid>
                        <Grid item xs={4}>
                          <Typography align="center">
                            {formatValue(row.from)}
                          </Typography>
                        </Grid>
                        <Grid
                          item
                          xs={4}
                          style={{
                            display: "flex",
                            justifyContent: "end",
                            paddingRight: 12,
                          }}
                        >
                          <Typography align="right">
                            {formatValue(row.to)}
                          </Typography>
                        </Grid>
                      </Grid>
                    </CardCommon>
                  ));
                })}
              </Grid>
            </Grid>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};

export default WithLoading(LogsDetailsInfoNode);
